import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { WeeklySummary } from '../types/jobs';

interface TimeSummaryProps {
  summary: WeeklySummary;
  title?: string;
}

const TimeSummary: React.FC<TimeSummaryProps> = ({ 
  summary, 
  title 
}) => {
  const { t } = useTranslation();

  const formatHours = (totalMinutes: number) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours}h ${minutes}m`;
  };

  const defaultTitle = title || t('thisWeeksTime');

  return (
    <div className="card">
      <h2 className="text-xl font-semibold text-gray-900 mb-4">
        {defaultTitle}
      </h2>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-blue-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-blue-600">{t('totalHours')}</p>
                <p className="text-lg font-semibold text-blue-900">
                  {formatHours(summary.totalHours)}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-600">{t('jobsCompleted')}</p>
                <p className="text-lg font-semibold text-green-900">
                  {summary.jobsCompleted}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-purple-600">{t('jobsScheduled')}</p>
                <p className="text-lg font-semibold text-purple-900">
                  {summary.jobsScheduled}
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-orange-50 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-orange-600">{t('averageHoursPerDay')}</p>
                <p className="text-lg font-semibold text-orange-900">
                  {formatHours(summary.averageHoursPerDay)}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">{t('weeklySummary')}</span>
            <span className="text-sm font-medium text-gray-900">
              {formatHours(summary.totalHours)} / {summary.jobsCompleted} {t('jobsCompleted')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSummary; 